/* SF Product UI repair — additive, reversible; no cart/checkout requests. */
(() => {
  'use strict';
  if (window.__sfProductUiRepair) return;
  window.__sfProductUiRepair = true;
  document.documentElement.classList.add('sf-product-ui-repair');
  const controllers = new Map();
  let scheduled = 0;

  function scheduleClearance() {
    if (scheduled) return;
    scheduled = requestAnimationFrame(() => {
      scheduled = 0;
      let height = 0;
      for (const host of controllers.keys()) {
        const bar = host.querySelector('.yg-current-variant-container.active');
        if (bar && host.isConnected) height = Math.max(height, bar.getBoundingClientRect().height);
      }
      const value = height ? `${Math.ceil(height) + 12}px` : '0px';
      if (document.documentElement.style.getPropertyValue('--sf-product-atc-clearance') !== value) {
        document.documentElement.style.setProperty('--sf-product-atc-clearance', value);
      }
    });
  }

  function mountBar(host) {
    if (controllers.has(host) || typeof host.updateEl !== 'function' || !host.querySelector('.yg-current-variant-container') || !host.visibleAtc?.clear) return;
    const lifecycle = new AbortController();
    const signal = lifecycle.signal;
    const original = {isShow: host.isShow, updateEl: host.updateEl, updateVariantContainerVisibility: host.updateVariantContainerVisibility};
    const originalFormId = host.$sectionsEl?.['atc-button']?.id;
    let form = null;
    let bar = null;
    let revision = 0;
    let pending = null;
    let stale = false;
    const resize = new ResizeObserver(scheduleClearance);
    const fresh = () => ({
      'atc-button': (originalFormId && document.getElementById(originalFormId)) || document.querySelector('product-info [is="product-form"]') || document.querySelector('product-rerender [is="product-form"]'),
      variantContainer: host.querySelector('.yg-current-variant-container'),
      listAnchor: host.querySelector('.yg-tab-list')
    });
    // Original getter cached the detached bar after replaceWith(). Always resolve current DOM.
    Object.defineProperty(host, '$sectionsEl', {configurable: true, get: fresh});

    function refresh() {
      if (!host.isConnected) return;
      const current = fresh();
      if (current.variantContainer !== bar) {
        if (bar) resize.unobserve(bar);
        bar = current.variantContainer;
        if (bar) resize.observe(bar);
      }
      const target = current['atc-button'];
      let mainVisible = false;
      if (target?.isConnected) {
        const rect = target.getBoundingClientRect();
        const visible = Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
        mainVisible = rect.height > 0 && rect.width > 0 && visible / rect.height >= 0.1;
      }
      if (bar) {
        const active = !stale && !!target && !mainVisible && !host.isFooterVisible;
        bar.classList.toggle('active', active);
        bar.setAttribute('aria-hidden', String(!active));
        bar.inert = !active;
      }
      scheduleClearance();
    }

    function bind() {
      const target = fresh()['atc-button'];
      if (form !== target || !host.observer) {
        form = target;
        host.observer?.disconnect();
        host.visibleAtc.clear();
        host.observer = new IntersectionObserver(refresh, {threshold: [0, 0.1, 1]});
        if (form) host.observer.observe(form);
      }
      refresh();
    }

    host.isShow = bind;
    host.updateVariantContainerVisibility = refresh;
    host.updateEl = async function(event) {
      const detail = event.detail || {};
      if (detail.formId && form?.id && detail.formId !== form.id) return;
      const token = ++revision;
      pending?.abort();
      pending = null;
      const install = node => {
        if (!host.isConnected || token !== revision || !node) return;
        const replacement = node.cloneNode(true);
        stale = false;
        host.querySelector('.yg-current-variant-container')?.replaceWith(replacement);
        bind();
        requestAnimationFrame(bind);
      };
      if (detail.scrollTabsEl) {
        install(detail.scrollTabsEl);
        return;
      }
      // Older theme events omit scrollTabsEl. Fetch only rendered HTML for the selected variant.
      const variantId = detail.variant?.id;
      if (!variantId) { bind(); return; }
      stale = true;
      refresh();
      const url = new URL(location.href);
      url.searchParams.set('variant', String(variantId));
      const sectionId = host.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      if (sectionId) url.searchParams.set('section_id', sectionId);
      pending = new AbortController();
      try {
        const response = await fetch(url, {signal: pending.signal, credentials: 'same-origin'});
        if (!response.ok) throw new Error(`Section response ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextBar = html.querySelector('.yg-current-variant-container');
        if (!nextBar) throw new Error('Variant bar missing in section response');
        install(nextBar);
      } catch (error) {
        if (error.name !== 'AbortError' && token === revision) {
          // Do not show an old price/variant when refresh failed.
          stale = true;
          refresh();
          console.warn('[SF Product UI] Variant bar refresh failed.', error);
          scheduleClearance();
        }
      }
    };

    const mutations = new MutationObserver(bind);
    mutations.observe(host, {childList: true, subtree: true});
    document.addEventListener('product:rerender', bind, {signal});
    window.addEventListener('resize', bind, {signal});
    window.addEventListener('scroll', refresh, {signal, passive: true});
    window.visualViewport?.addEventListener('resize', refresh, {signal});
    // Preserve native button delegation but prevent unavailable variants being activated.
    host.addEventListener('click', event => {
      const button = event.target.closest('.yg-add-to-cart, .yg-buy-it-now');
      if (button && (button.classList.contains('disabled') || button.getAttribute('aria-disabled') === 'true')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, {signal, capture: true});
    controllers.set(host, () => {
      pending?.abort(); lifecycle.abort(); mutations.disconnect(); resize.disconnect();
      host.observer?.disconnect();
      delete host.$sectionsEl;
      Object.assign(host, original);
    });
    bind();
  }

  function scan() {
    for (const [host, stop] of controllers) if (!host.isConnected) {stop(); controllers.delete(host);}
    document.querySelectorAll('.yg-navgation-03 yg-anchor-tab').forEach(mountBar);
    scheduleClearance();
  }
  customElements.whenDefined('yg-anchor-tab').then(scan);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(scan));
  scan();
})();
